import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@t-one-internal/design-system';
import { PUBLIC_API_BASE_URL } from '../../api/apiClient';

interface ResetPasswordButtonProps {
  userEmail: string;
  className?: string;
  children?: React.ReactNode;
}

const RATE_LIMITS = [5 * 60 * 1000, 7 * 60 * 1000, 10 * 60 * 1000];
const GRACE_PERIOD = 1 * 60 * 1000;
const STORAGE_KEY = 'reset_password_state';

interface ResetPasswordState {
  lastRequestTime: number | null;
  penaltyLevel: number;
}

export const ResetPasswordButton: React.FC<ResetPasswordButtonProps> = ({ userEmail, className, children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [state, setState] = useState<ResetPasswordState>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { return JSON.parse(stored); } catch { return { lastRequestTime: null, penaltyLevel: 0 }; }
    }
    return { lastRequestTime: null, penaltyLevel: 0 };
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (!state.lastRequestTime) return;
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - (state.lastRequestTime || 0);
      const currentLimit = RATE_LIMITS[state.penaltyLevel];
      const remaining = currentLimit - elapsed;
      if (remaining <= 0) {
        setTimeRemaining(null);
        if (elapsed > currentLimit + GRACE_PERIOD) setState(prev => ({ ...prev, penaltyLevel: 0 }));
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [state.lastRequestTime, state.penaltyLevel]);

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleResetPassword = async () => {
    if (timeRemaining && timeRemaining > 0) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${PUBLIC_API_BASE_URL}/account/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const text = await response.text();
      const data = text ? JSON.parse(text) : { success: true };
      if (data.success) {
        setShowSuccessDialog(true);
        const now = Date.now();
        setState((prev) => {
          let newPenaltyLevel = prev.penaltyLevel;
          if (prev.lastRequestTime) {
            const elapsed = now - prev.lastRequestTime;
            const currentLimit = RATE_LIMITS[prev.penaltyLevel];
            if (elapsed < currentLimit + GRACE_PERIOD) {
              newPenaltyLevel = Math.min(prev.penaltyLevel + 1, RATE_LIMITS.length - 1);
            } else {
              newPenaltyLevel = 0;
            }
          }
          return { lastRequestTime: now, penaltyLevel: newPenaltyLevel };
        });
      } else {
        alert(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = (timeRemaining !== null && timeRemaining > 0) || isLoading;

  return (
    <>
      <button
        onClick={handleResetPassword}
        disabled={isDisabled}
        className={className || `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
        }`}
        title={timeRemaining ? `Please wait ${formatTime(timeRemaining)}` : 'Send password reset email'}
      >
        {children || (isLoading ? 'Sending...' : timeRemaining ? `Wait ${formatTime(timeRemaining)}` : 'Reset Password')}
      </button>

      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Reset Email Sent</DialogTitle>
            <DialogDescription>
              A password reset link has been sent to your email address: <strong>{userEmail}</strong>
              <br /><br />
              Please check your inbox and follow the instructions to reset your password.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};
